(() => {
  document.documentElement.classList.add('nx-js');

  const reducedMotion = () =>
    window.matchMedia('(prefers-reduced-motion: reduce)').matches ||
    document.body?.classList.contains('nx-motion--off') ||
    (window.innerWidth <= 900 && document.body?.classList.contains('nx-motion-mobile-reduced'));

  let revealObserver;
  let storyObserver;
  const parallaxCleanup = new WeakMap();
  const countdownCleanup = new WeakMap();

  function getRevealObserver() {
    if (revealObserver || !('IntersectionObserver' in window)) return revealObserver;
    revealObserver = new IntersectionObserver(
      (entries, observer) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) return;
          entry.target.classList.add('is-visible');
          observer.unobserve(entry.target);
        });
      },
      { threshold: 0.12, rootMargin: '0px 0px -8% 0px' },
    );
    return revealObserver;
  }

  function initReveal(scope = document) {
    const nodes = scope.querySelectorAll('[data-nx-reveal]:not(.is-visible)');
    if (!nodes.length) return;
    if (reducedMotion() || !('IntersectionObserver' in window)) {
      nodes.forEach((node) => node.classList.add('is-visible'));
      return;
    }
    const observer = getRevealObserver();
    // Cascade : chaque élément d'un même parent est décalé (réglage nx_motion_stagger).
    const seen = new Map();
    nodes.forEach((node) => {
      const parent = node.parentElement || document.body;
      const index = seen.get(parent) ?? 0;
      node.style.setProperty('--nx-reveal-index', String(index));
      seen.set(parent, index + 1);
      observer.observe(node);
    });
  }

  function initParallax(scope = document) {
    if (reducedMotion() || document.body?.classList.contains('nx-motion--light')) return;
    scope.querySelectorAll('[data-nx-parallax]:not([data-nx-parallax-ready])').forEach((node) => {
      node.dataset.nxParallaxReady = 'true';
      const speed = Number(node.dataset.nxParallax || 0.12);
      let frame = 0;
      const update = () => {
        const rect = node.closest('.nx-section, .nx-hero')?.getBoundingClientRect() || node.getBoundingClientRect();
        const progress = (window.innerHeight - rect.top) / (window.innerHeight + rect.height) - 0.5;
        node.style.setProperty('--nx-parallax-y', `${progress * speed * -100}px`);
        node.style.transform = 'translate3d(0,var(--nx-parallax-y),0)';
        frame = 0;
      };
      const onScroll = () => {
        if (frame) return;
        frame = requestAnimationFrame(update);
      };
      update();
      window.addEventListener('scroll', onScroll, { passive: true });
      parallaxCleanup.set(node, () => {
        window.removeEventListener('scroll', onScroll);
        if (frame) cancelAnimationFrame(frame);
        parallaxCleanup.delete(node);
      });
    });
  }


  function initTabs(scope = document) {
    scope.querySelectorAll('[data-nx-tabs]:not([data-nx-tabs-ready])').forEach((tabs) => {
      tabs.dataset.nxTabsReady = 'true';
      const buttons = [...tabs.querySelectorAll('[role="tab"]')];
      const panels = [...tabs.querySelectorAll('[role="tabpanel"]')];
      if (!buttons.length) return;

      const activate = (button, moveFocus = false) => {
        buttons.forEach((item) => {
          const selected = item === button;
          item.setAttribute('aria-selected', String(selected));
          item.tabIndex = selected ? 0 : -1;
        });
        panels.forEach((panel) => {
          const active = panel.id === button.getAttribute('aria-controls');
          panel.classList.toggle('is-active', active);
          panel.setAttribute('aria-hidden', String(!active));
        });
        if (moveFocus) button.focus();
      };

      buttons.forEach((button, index) => {
        button.addEventListener('click', () => activate(button));
        button.addEventListener('keydown', (event) => {
          let nextIndex = index;
          if (event.key === 'ArrowRight' || event.key === 'ArrowDown') nextIndex = (index + 1) % buttons.length;
          else if (event.key === 'ArrowLeft' || event.key === 'ArrowUp') nextIndex = (index - 1 + buttons.length) % buttons.length;
          else if (event.key === 'Home') nextIndex = 0;
          else if (event.key === 'End') nextIndex = buttons.length - 1;
          else return;
          event.preventDefault();
          activate(buttons[nextIndex], true);
        });
      });

      activate(buttons.find((button) => button.getAttribute('aria-selected') === 'true') || buttons[0]);
    });
  }

  function getStoryObserver() {
    if (storyObserver || !('IntersectionObserver' in window)) return storyObserver;
    storyObserver = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) return;
          const story = entry.target.closest('[data-nx-scroll-story]');
          if (!story) return;
          const index = entry.target.dataset.nxStoryStep;
          story.querySelectorAll('[data-nx-story-step]').forEach((step) => {
            step.classList.toggle('is-active', step === entry.target);
          });
          story.querySelectorAll('[data-nx-story-media]').forEach((media) => {
            media.classList.toggle('is-active', media.dataset.nxStoryMedia === index);
          });
        });
      },
      { threshold: 0.55, rootMargin: '-15% 0px -15% 0px' },
    );
    return storyObserver;
  }

  function initScrollStories(scope = document) {
    const steps = scope.querySelectorAll('[data-nx-story-step]:not([data-nx-story-ready])');
    if (!steps.length) return;
    if (!('IntersectionObserver' in window)) {
      steps.forEach((step) => step.classList.add('is-active'));
      return;
    }
    const observer = getStoryObserver();
    steps.forEach((step) => {
      step.dataset.nxStoryReady = 'true';
      observer.observe(step);
    });
  }

  function initDetails(scope = document) {
    scope.querySelectorAll('.nx-hotspot__point:not([data-nx-details-ready])').forEach((point) => {
      point.dataset.nxDetailsReady = 'true';
      point.addEventListener('toggle', () => {
        if (!point.open) return;
        point.closest('.nx-hotspot')?.querySelectorAll('.nx-hotspot__point[open]').forEach((other) => {
          if (other !== point) other.removeAttribute('open');
        });
      });
    });
  }

  function initCounters(scope = document) {
    const nodes = scope.querySelectorAll('[data-nx-counter]:not([data-nx-counter-ready])');
    if (!nodes.length) return;
    const run = (node) => {
      node.dataset.nxCounterReady = 'true';
      const raw = node.dataset.nxCounter || '';
      const target = Number(raw.replace(/[^0-9.,-]/g, '').replace(',', '.'));
      if (!Number.isFinite(target) || reducedMotion()) return;
      const decimals = (String(target).split('.')[1] || '').length;
      const duration = 1100;
      const start = performance.now();
      const step = (now) => {
        const progress = Math.min(1, (now - start) / duration);
        const eased = 1 - Math.pow(1 - progress, 3);
        node.textContent = (target * eased).toFixed(decimals);
        if (progress < 1) requestAnimationFrame(step);
      };
      requestAnimationFrame(step);
    };
    if (!('IntersectionObserver' in window)) {
      nodes.forEach(run);
      return;
    }
    const observer = new IntersectionObserver(
      (entries) =>
        entries.forEach((entry) => {
          if (!entry.isIntersecting) return;
          run(entry.target);
          observer.unobserve(entry.target);
        }),
      { threshold: 0.35 },
    );
    nodes.forEach((node) => observer.observe(node));
  }

  function initCountdowns(scope = document) {
    scope.querySelectorAll('[data-nx-countdown]:not([data-nx-countdown-ready])').forEach((timer) => {
      timer.dataset.nxCountdownReady = 'true';
      const end = new Date(timer.dataset.end || '').getTime();
      const expiredNode = timer.querySelector('.nx-countdown__expired');
      if (!Number.isFinite(end)) {
        if (expiredNode) {
          expiredNode.hidden = false;
          expiredNode.textContent = timer.dataset.invalid || '';
        }
        return;
      }
      const fields = {
        days: timer.querySelector('[data-days]'),
        hours: timer.querySelector('[data-hours]'),
        minutes: timer.querySelector('[data-minutes]'),
        seconds: timer.querySelector('[data-seconds]'),
      };
      let interval;
      const update = () => {
        const remaining = Math.max(0, end - Date.now());
        const values = {
          days: Math.floor(remaining / 86400000),
          hours: Math.floor((remaining % 86400000) / 3600000),
          minutes: Math.floor((remaining % 3600000) / 60000),
          seconds: Math.floor((remaining % 60000) / 1000),
        };
        Object.entries(values).forEach(([key, value]) => {
          if (fields[key]) fields[key].textContent = String(value).padStart(2, '0');
        });
        if (remaining > 0) return;
        clearInterval(interval);
        const clock = timer.querySelector('.nx-countdown__clock');
        if (clock) clock.hidden = true;
        if (timer.dataset.hideExpired === 'true') timer.closest('.shopify-section')?.setAttribute('hidden', '');
        else if (expiredNode) {
          expiredNode.hidden = false;
          expiredNode.textContent = timer.dataset.expired || '';
        }
      };
      update();
      interval = window.setInterval(update, 1000);
      countdownCleanup.set(timer, () => { clearInterval(interval); countdownCleanup.delete(timer); });
    });
  }

  function initFooterAccordion(scope = document) {
    var items = scope.querySelectorAll('.nx-footer__col-details');
    if (!items.length) return;
    var isMobile = window.matchMedia('(max-width: 989px)').matches;
    items.forEach((el) => {
      el.classList.add('nx-js-collapsible');
      // Etat initial uniquement : on ne referme jamais un panneau que la
      // personne a deja ouvert a la main en redimensionnant sa fenetre.
      if (isMobile && !el.dataset.nxUserToggled) el.removeAttribute('open');
      if (!el.dataset.nxBound) {
        el.dataset.nxBound = 'true';
        el.addEventListener('toggle', () => { el.dataset.nxUserToggled = 'true'; });
      }
    });
  }

  function init(scope = document) {
    initReveal(scope);
    initParallax(scope);
    initTabs(scope);
    initScrollStories(scope);
    initDetails(scope);
    initCounters(scope);
    initCountdowns(scope);
    initFooterAccordion(scope);
  }

  function cleanupScope(scope) {
    scope.querySelectorAll('[data-nx-parallax]').forEach((node) => parallaxCleanup.get(node)?.());
    scope.querySelectorAll('[data-nx-reveal]').forEach((node) => revealObserver?.unobserve(node));
    scope.querySelectorAll('[data-nx-story-step]').forEach((node) => storyObserver?.unobserve(node));
    scope.querySelectorAll('[data-nx-countdown]').forEach((node) => countdownCleanup.get(node)?.());
  }

  document.addEventListener('DOMContentLoaded', () => init(), { once: true });
  document.addEventListener('shopify:section:load', (event) => init(event.target));
  document.addEventListener('shopify:section:unload', (event) => cleanupScope(event.target));
})();
